/**
 * Outbound link safety.
 *
 * Canon `docs/SECURITY.md` § URLs allows only `http`/`https` for a deal destination, and
 * core validates that before a deal is ever approved. This is the browser-side repeat of
 * the same rule, and it exists because of where the value lands: React does not sanitise
 * `href`, so a `javascript:` URL that reached this bundle would execute on click. Canon
 * `AGENTS.md` § 12 says to treat all input as untrusted — a response body is input, and a
 * seller's own deal row is no more trusted here than a stranger's.
 *
 * Identical in behaviour to `dealbid-web`'s copy on purpose: a URL the public board
 * refuses to link must not become clickable on the seller desk.
 */

const ALLOWED_PROTOCOLS = new Set(["http:", "https:"]);

/**
 * Returns the URL when it is a safe outbound destination, otherwise `null`.
 *
 * A `null` is a deliberate outcome, not an error to swallow quietly: the caller renders the
 * deal without a link rather than shipping one that cannot be trusted.
 */
export function safeExternalUrl(url: string | null | undefined): string | null {
  if (typeof url !== "string" || url.trim() === "") return null;
  try {
    const parsed = new URL(url);
    return ALLOWED_PROTOCOLS.has(parsed.protocol) ? parsed.toString() : null;
  } catch {
    // Relative or malformed. A deal destination is always absolute, so this is not one.
    return null;
  }
}

/** Hostname for display (`acme.example`), or `null` when the URL is not usable. */
export function displayHost(url: string | null | undefined): string | null {
  const safe = safeExternalUrl(url);
  if (safe === null) return null;
  return new URL(safe).hostname.replace(/^www\./, "");
}
